import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, IndianRupee, FileText, RefreshCw } from "lucide-react";
import { api, apiError, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const Row = ({ item, onAction, actionLabel, testidPrefix }) => (
  <div
    className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3"
    data-testid={`${testidPrefix}-${item.id}`}
  >
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold">
          {item.invoice_no
            ? `${item.invoice_no} · ${item.client}`
            : `${item.client} · ${item.campaign}${item.period ? ` · ${item.period}` : ""}`}
        </div>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span>Due {item.due}</span>
        <Badge
          variant="outline"
          className={`rounded-md border ${
            item.days_overdue > 7
              ? "border-red-200 bg-red-50 text-red-700"
              : "border-amber-200 bg-amber-50 text-amber-700"
          }`}
        >
          {item.days_overdue === 0 ? "due today" : `${item.days_overdue}d overdue`}
        </Badge>
        <span>{money(item.amount, item.currency)}</span>
        {item.notes && <span className="italic">“{item.notes}”</span>}
      </div>
    </div>
    <Button
      size="sm"
      data-testid={`${testidPrefix}-action-${item.id}`}
      className="rounded-md bg-slate-900 hover:bg-slate-800 active:scale-95"
      onClick={() => onAction(item)}
    >
      <CheckCircle2 className="mr-2 h-4 w-4" /> {actionLabel}
    </Button>
  </div>
);

const Empty = ({ label }) => <div className="px-5 py-10 text-sm text-slate-500">{label}</div>;

export default function Reminders() {
  const [data, setData] = useState(null);

  const load = () => api.get("/reminders").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const act = async (fn, label) => {
    try {
      await fn();
      toast.success(label);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const c = data?.counts || {};

  return (
    <div data-testid="reminders-page">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            Daily Follow-ups · as of {data?.as_of || "—"}
          </div>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl">Reminders</h1>
          <p className="mt-3 max-w-2xl text-sm text-slate-600">
            Any month whose validate date or payment date has passed shows up here every day until you
            clear it, along with overdue invoices.
          </p>
        </div>
        <Button
          variant="outline"
          data-testid="refresh-reminders-button"
          className="rounded-md border-slate-300 active:scale-95"
          onClick={load}
        >
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-3">
        {[
          ["Validation overdue", c.validations || 0, AlertTriangle, "text-amber-600", "reminder-count-validations"],
          ["Payment overdue", c.payments || 0, IndianRupee, "text-red-600", "reminder-count-payments"],
          ["Invoices overdue", c.invoices || 0, FileText, "text-red-600", "reminder-count-invoices"],
        ].map(([label, value, Icon, tone, testid]) => (
          <div key={label} className="border border-slate-200 bg-white p-5" data-testid={testid}>
            <div className="flex items-start justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                {label}
              </span>
              <Icon className={`h-4 w-4 ${tone}`} />
            </div>
            <div className="mt-3 font-display text-3xl font-black tracking-tight">{value}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="validations">
        <TabsList className="mb-6 rounded-md bg-slate-100">
          <TabsTrigger value="validations" data-testid="tab-validations" className="rounded-md">
            Client Validation
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments" className="rounded-md">
            Campaign Payments
          </TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoice-reminders" className="rounded-md">
            Invoices
          </TabsTrigger>
        </TabsList>

        <TabsContent value="validations">
          <div className="border border-slate-200 bg-white" data-testid="validation-reminders">
            {(data?.validations || []).map((it) => (
              <Row
                key={it.id}
                item={it}
                actionLabel="Mark validated"
                testidPrefix="validation-reminder"
                onAction={(x) =>
                  act(
                    () => api.patch(`/validations/${x.id}/status`, { validation_status: "validated" }),
                    "Marked validated"
                  )
                }
              />
            ))}
            {!data?.validations?.length && <Empty label="No validations pending. All clear." />}
          </div>
        </TabsContent>

        <TabsContent value="payments">
          <div className="border border-slate-200 bg-white" data-testid="payment-reminders">
            {(data?.payments || []).map((it) => (
              <Row
                key={it.id}
                item={it}
                actionLabel="Mark paid"
                testidPrefix="payment-reminder"
                onAction={(x) =>
                  act(() => api.patch(`/validations/${x.id}/status`, { payment_status: "paid" }), "Marked paid")
                }
              />
            ))}
            {!data?.payments?.length && <Empty label="No campaign payments overdue." />}
          </div>
        </TabsContent>

        <TabsContent value="invoices">
          <div className="border border-slate-200 bg-white" data-testid="invoice-reminders">
            {(data?.invoices || []).map((it) => (
              <Row
                key={it.id}
                item={it}
                actionLabel="Mark paid"
                testidPrefix="invoice-reminder"
                onAction={(x) =>
                  act(() => api.patch(`/invoices/${x.id}/status`, { payment_status: "paid" }), "Invoice marked paid")
                }
              />
            ))}
            {!data?.invoices?.length && <Empty label="No invoices past their due date." />}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
